import AuthPageShell from 'components/auth/AuthPageShell'
import EmailInput from 'components/common/field/Email'
import UnderLink from 'components/common/UnderLink'
import { WizardStepChangeEventHandler } from 'components/types'
import { Location } from 'newTypes/location'
import { ChangeEvent, FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { EmailFieldValidationMessages } from 'types/fields'
import { FieldChangeEventAnswer } from 'types/general'

import { requestPasswordResetEmail } from '../api/services/passwordResetService'
import { i18n } from '../i18n'

const ResetPassword = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [emailValid, setEmailValid] = useState(false)
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleClose = () => navigate('/auth', { state: { email } })

  const title = t('common.resetYourPassword')
  const subtitle = t('common.resetPasswordSubtitle')

  const handleFieldChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | WizardStepChangeEventHandler
      | FieldChangeEventAnswer
      | Location
  ) => {
    if ('target' in e) {
      const { name, value } = e.target
      if (name === 'email') setEmail(value)
    } else if ('name' in e && 'value' in e && typeof e.value === 'string') {
      if (e.name === 'email') setEmail(e.value)
    }
  }

  const handleSendResetLink = async () => {
    if (!emailValid) return
    setLoading(true)
    const normalizedEmail = email.trim().toLowerCase()
    try {
      const result = await requestPasswordResetEmail(normalizedEmail)
      if (!result.success) {
        toast.error(
          result.message || i18n.translate('auth.resetPasswordFailed')
        )
        return
      }
      setEmailSent(true)
      toast.success(i18n.translate('auth.resetPasswordSuccess'))
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code?: string }).code ?? '')
          : ''
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message ?? '')
          : ''
      if (code === 'rate_limited') {
        toast.error(i18n.translate('auth.resetPasswordRateLimited'))
      } else {
        toast.error(message || i18n.translate('auth.resetPasswordFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (loading || !emailValid) return
    void handleSendResetLink()
  }

  return (
    <AuthPageShell title={title} subtitle={subtitle}>
      {!emailSent ? (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 [&>div]:!my-0"
        >
          <EmailInput
            name="email"
            label={i18n.translate('auth.yourEmail')}
            value={email}
            onChange={handleFieldChange}
            setIsValid={setEmailValid}
            errorMessage={t(
              EmailFieldValidationMessages.PleaseEnterAValidEmailFormat
            )}
            isFirstInput
          />
          <button
            type="submit"
            disabled={!emailValid || loading}
            className="mt-1 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-imovPrimary py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40"
          >
            {loading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              t('common.resetPassword')
            )}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
          >
            {t('common.cancel')}
          </button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-center text-sm leading-relaxed text-gray-600">
            {i18n.translate('auth.resetPasswordSuccess')}
          </p>
          <div className="text-center">
            <UnderLink
              label={i18n.translate('auth.logIn')}
              redirectTo="/auth"
              isUnderline
              className="font-semibold text-imovPrimary hover:text-imovPrimaryDark"
            />
          </div>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-gray-600">
        <UnderLink
          label={t('common.backToLogin')}
          redirectTo="/auth"
          isUnderline
          className="font-semibold text-imovPrimary hover:text-imovPrimaryDark"
        />
      </p>
    </AuthPageShell>
  )
}

export default ResetPassword
